import React from 'react';
import { 
  DocumentIcon, 
  ServerIcon, 
  ClockIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useServerAPI } from '../hooks/useServerData';
import { useServer } from '../contexts/ServerContext';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const Dashboard = () => {
  const { getCurrentServer, switchingServer } = useServer();
  
  // Using server-aware hooks that invalidate cache when server changes
  const { data: backupStats, loading: loadingBackups, error: backupsError } = useServerAPI(
    '/api/backups/stats',
    { refetchInterval: 30000 }
  );

  const { data: system, loading: loadingSystem, error: systemError } = useServerAPI(
    '/api/system/status',
    { refetchInterval: 10000 }
  );

  const { data: cronStatus, error: cronError } = useServerAPI(
    '/api/server/cron/status',
    { refetchInterval: 60000 }
  );

  const { data: dockerStatus, error: dockerError } = useServerAPI(
    '/api/server/docker/status',
    { refetchInterval: 30000 }
  );

  const stats = backupStats;
  const cron = cronStatus;
  const docker = dockerStatus;

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <div className="card">
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-2 sm:p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-5 w-5 sm:h-6 sm:w-6 text-${color}-600`} />
        </div>
        <div className="ml-3 sm:ml-4 min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{title}</p>
          <p className="text-lg sm:text-2xl font-semibold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs sm:text-sm text-gray-500 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const StatusIndicator = ({ active, label }) => (
    <div className="flex items-center space-x-2">
      {active ? (
        <CheckCircleIcon className="h-5 w-5 text-green-500" />
      ) : (
        <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
      )}
      <span className={`text-sm ${active ? 'text-green-700' : 'text-red-700'}`}>
        {label}
      </span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Visão geral do sistema: <strong>{getCurrentServer()?.name}</strong>
        </p>
        {switchingServer && (
          <div className="mt-2 text-sm text-blue-600 flex items-center">
            <ClockIcon className="h-4 w-4 mr-1 animate-spin" />
            Carregando dados do servidor...
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          title="Total de Backups"
          value={loadingBackups ? '...' : backupsError ? 'Erro' : stats?.total?.totalFiles || 0}
          subtitle={loadingBackups ? '' : backupsError ? 'Falha ao carregar' : stats?.total?.totalSizeFormatted || '0 B'}
          icon={DocumentIcon}
          color={backupsError ? 'red' : 'blue'}
        />
        
        <StatCard
          title="PostgreSQL"
          value={loadingBackups ? '...' : backupsError ? 'Erro' : stats?.stats?.postgresql?.count || 0}
          subtitle={loadingBackups ? '' : backupsError ? 'Falha ao carregar' : stats?.stats?.postgresql?.sizeFormatted || '0 B'}
          icon={ChartBarIcon}
          color={backupsError ? 'red' : 'green'}
        />
        
        <StatCard
          title="Portainer"
          value={loadingBackups ? '...' : backupsError ? 'Erro' : stats?.stats?.portainer?.count || 0}
          subtitle={loadingBackups ? '' : backupsError ? 'Falha ao carregar' : stats?.stats?.portainer?.sizeFormatted || '0 B'}
          icon={ServerIcon}
          color={backupsError ? 'red' : 'purple'}
        />
        
        <StatCard
          title="Uptime"
          value={loadingSystem ? '...' : systemError ? 'Erro' : system?.uptime || 'Indisponível'}
          subtitle={loadingSystem ? 'Carregando...' : systemError ? `Erro: ${typeof systemError === 'string' ? systemError : systemError.message || 'Falha ao conectar'}` : `Hostname: ${system?.hostname || 'N/A'}`}
          icon={ClockIcon}
          color={systemError ? 'red' : 'indigo'}
        />
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Info */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status do Sistema</h3>
          
          {loadingSystem ? (
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ) : systemError ? (
            <div className="text-center py-4">
              <div className="text-red-500 mb-2">
                <ExclamationTriangleIcon className="h-12 w-12 mx-auto" />
              </div>
              <p className="text-red-700 font-medium">Erro ao conectar com o servidor</p>
              <p className="text-sm text-gray-600 mt-1">
                {typeof systemError === 'string' ? systemError : systemError.message || 'Verifique a conexão e tente novamente'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">CPU Load (1min):</span>
                <span className="font-medium">{system?.loadAverage?.['1min'] || 'N/A'}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Memória Usada:</span>
                <span className="font-medium">
                  {system?.memory ? 
                    `${system.memory.usagePercent}% (${system.memory.used}MB/${system.memory.total}MB)` : 
                    'N/A'
                  }
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Disco Usado:</span>
                <span className="font-medium">
                  {system?.disk ? `${system.disk.usagePercent}% (${system.disk.used}/${system.disk.total})` : 'N/A'}
                </span>
              </div>

              {/* Progress bars */}
              {system?.memory && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Memória</span>
                    <span>{system.memory.usagePercent}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        system.memory.usagePercent > 80 ? 'bg-red-500' :
                        system.memory.usagePercent > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${system.memory.usagePercent}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {system?.disk && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Disco</span>
                    <span>{system.disk.usagePercent}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        system.disk.usagePercent > 80 ? 'bg-red-500' :
                        system.disk.usagePercent > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${system.disk.usagePercent}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Services Status */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status dos Serviços</h3>
          
          {cronError || dockerError ? (
            <div className="text-center py-4">
              <div className="text-yellow-500 mb-2">
                <ExclamationTriangleIcon className="h-8 w-8 mx-auto" />
              </div>
              <p className="text-yellow-700 font-medium">Alguns serviços indisponíveis</p>
              <p className="text-sm text-gray-600 mt-1">
                {cronError && 'Backup automático: erro de conexão'}
                {cronError && dockerError && ' | '}
                {dockerError && 'Docker: erro de conexão'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <StatusIndicator 
                active={cron?.active} 
                label={`Backup Automático ${cron?.active ? 'Ativo' : 'Inativo'}`}
              />
              
              <StatusIndicator 
                active={docker?.services?.length > 0} 
                label={`Docker (${docker?.services?.length || 0} containers)`}
              />
              
              {docker?.services?.slice(0, 3).map((service, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-gray-600">{service.name}:</span>
                  <span className={`font-medium ${
                    service.status && service.status.includes('Up') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {service.status && service.status.includes('Up') ? 'Online' : 'Offline'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Backups */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Backups Recentes</h3>
        
        {loadingBackups ? (
          <div className="animate-pulse">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex justify-between py-2">
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/5"></div>
              </div>
            ))}
          </div>
        ) : backupsError ? (
          <div className="text-center py-4">
            <div className="text-red-500 mb-2">
              <ExclamationTriangleIcon className="h-8 w-8 mx-auto" />
            </div>
            <p className="text-red-700 font-medium">Erro ao carregar backups</p>
            <p className="text-sm text-gray-600 mt-1">
              {typeof backupsError === 'string' ? backupsError : backupsError.message || 'Não foi possível acessar o sistema de backups'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {stats?.recent?.length > 0 ? (
              stats.recent.map((backup, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{backup.name}</p>
                    <p className="text-xs text-gray-500">{backup.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">{backup.size}</p>
                    <p className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(backup.date), { 
                        addSuffix: true, 
                        locale: ptBR 
                      })}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">Nenhum backup encontrado</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;